import { isCnArea } from '@/utils/envHelper';

import {
  Commodity,
  commodityCata,
  CommodityCurrency,
  CommodityDate,
  DurationType,
  FunctionName,
  functionPriceConfig,
  Resolution,
  UserIdentity,
} from '../const';
import { featureIntl } from '../intl';
import { FunctionLimitMap, LimitConfig, ValueType, ValueTypeOutPut } from '../type';

export const ResolutionMap = {
  '480': '480p',
  '720': '720p',
  '1080': '1080p',
  '1440': '2k',
  '2160': '4k',
} as const;

/**
 * @description 将服务端返回功能点数据数据转换成map映射表
 */
const defaultCommodityData = {
  commodityCode: '',
  title: '',
  desc: '',
  modelCode: '',
  originalPrice: 0,
  realPrice: 0,
  discount: 0,
  sort: 0,
  commodityType: 0,
  increaseTime: 0,
  content: {
    durationType: 2,
    category: 'freeVip',
    point: 0,
    label: '',
    currency: isCnArea ? 'CNY' : 'USD',
  },
} as Commodity[number];

function filterVipCommodity(
  item: Commodity[number],
  key: UserIdentity,
  dateType: DurationType,
) {
  const currency = isCnArea ? CommodityCurrency.CNY : CommodityCurrency.USD;
  return (
    // 用户vip身份 && 商品类型 && 货币类型 && 非ai积分包类型
    item?.content?.category === key &&
    (key === 'freeVip' ? true : item?.content?.durationType === dateType) &&
    item?.content?.currency === currency &&
    item.content.commodityName !== 'single_cloud_right'
  );
}

const transferLimitDataToMap = (limitConfigData: FunctionLimitMap) => {
  const { commodity, ...limitConfig } = limitConfigData;

  return Object.keys(limitConfig).reduce((newConfig, key) => {
    const functionLimitList = limitConfig[key as UserIdentity];
    if (functionLimitList) {
      const limit = functionLimitList.reduce(
        (acc, limitItem) => {
          const { funcName, functionLimit } = limitItem;
          const isResolutionKey = funcName.includes('Resolution');
          // 严格保证 functionLimit 的类型和 ValueType 对应
          const value: ValueTypeOutPut<typeof funcName> = isResolutionKey
            ? ResolutionMap[functionLimit as Resolution]
            : (functionLimit ?? true);

          return {
            ...acc,
            [funcName]: value,
          };
        },
        {} as { [K in FunctionName]: ValueTypeOutPut<K> },
      );

      // 获取当前区域的商品信息
      const commodityDataYear = commodity?.filter((item) =>
        filterVipCommodity(item, key as UserIdentity, CommodityDate.YEAR),
      );
      const commodityDataMonth = commodity?.filter((item) =>
        filterVipCommodity(item, key as UserIdentity, CommodityDate.MONTH),
      );
      const commodityDataTWOWeek = commodity?.filter((item) =>
        filterVipCommodity(item, key as UserIdentity, CommodityDate.TWOWEEK),
      );

      let commodityData: any = {};
      if (commodityDataYear.length) {
        commodityData.year = commodityDataYear;
      } else if (key === 'freeVip' || key === 'enterpriseVip') {
        commodityData.year = [
          {
            ...defaultCommodityData,
            content: { ...defaultCommodityData.content, durationType: 5 },
          },
        ];
      }
      if (commodityDataMonth.length) {
        commodityData.month = commodityDataMonth;
      } else if (key === 'freeVip' || key === 'enterpriseVip') {
        commodityData.month = [defaultCommodityData];
      }
      if (commodityDataTWOWeek.length) {
        commodityData.twoweek = commodityDataTWOWeek;
      } else if (key === 'freeVip' || key === 'enterpriseVip') {
        commodityData.twoweek = [
          {
            ...defaultCommodityData,
            content: { ...defaultCommodityData.content, durationType: 1 },
          },
        ];
      }
      newConfig[key as UserIdentity] = {
        ...limit,
        priceConfig: functionPriceConfig,
        commodityData,
        featureDesc: featureIntl[isCnArea ? 'cn' : 'us'][
          key as UserIdentity
        ] as LimitConfig[UserIdentity]['featureDesc'],
        // commodityDataYear.length &&
        // commodityDataMonth.length &&
        // commodityDataTWOWeek.length
        //   ? {
        //       year: commodityDataYear,
        //       month: commodityDataMonth,
        //       twoweek: commodityDataTWOWeek,
        //     }
        //   : {
        //       year: [
        //         {
        //           ...defaultCommodityData,
        //           content: { ...defaultCommodityData.content, durationType: 5 },
        //         },
        //       ],
        //       month: [defaultCommodityData],
        //       twoweek: [
        //         {
        //           ...defaultCommodityData,
        //           content: { ...defaultCommodityData.content, durationType: 1 },
        //         },
        //       ],
        //     },
      };
    }
    return newConfig;
  }, {} as LimitConfig);
};

/**
 * 获取一次性购买的商品配置
 * @param limitConfigData 功能限制配置数据
 * @returns 匹配的商品数组，如果没有匹配项则返回空数组
 */
const getSinglePurchaseCommodities = (limitConfigData: FunctionLimitMap) =>
  (limitConfigData.commodity || []).filter(
    (item) =>
      // 海外和国内积分包购买
      item.content.commodityName === 'single_cloud_right' &&
      item.content.currency === (isCnArea ? 'CNY' : 'USD'),
  );
export { getSinglePurchaseCommodities, transferLimitDataToMap };
